/**
 * @file analyticsSnapshotService.ts
 * @description Typed client for the Phase 1 analytics-snapshot comparison endpoint.
 * Backing table/service: backend/src/modules/analytics/analytics-snapshot-query.service.ts.
 * Only ACTUAL (non-backfilled) snapshots are ever compared — see
 * docs/ANALYTICS_IMPLEMENTATION_PLAN.md §7.3. Callers must treat
 * `available: false` as "not enough history yet", not an error.
 */
import apiClient from './apiClient';

export type AnalyticsSnapshotDomain = 'asset' | 'license' | 'inventory';

export interface MetricComparison {
  current: number;
  previous: number;
  absoluteChange: number;
  /** null when the previous value was 0 — percentage change is undefined in that case. */
  percentChange: number | null;
}

export interface SnapshotComparisonResult {
  available: boolean;
  reason?: string;
  currentDate?: string;
  previousDate?: string;
  metrics?: Record<string, MetricComparison | Record<string, MetricComparison>>;
}

function toDateOnly(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export interface AnalyticsSnapshotRunLogEntry {
  id: number;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  triggeredBy: 'scheduled' | 'manual';
  triggeredByUserId: number | null;
  snapshotDate: string;
  succeededDomains: AnalyticsSnapshotDomain[];
  failedDomains: { domain: AnalyticsSnapshotDomain; error: string }[];
  rowCounts: Record<string, number>;
  status: 'success' | 'partial' | 'failed';
  createdAt: string;
}

export interface SnapshotJobHealth {
  lastAttempt: AnalyticsSnapshotRunLogEntry | null;
  lastSuccess: AnalyticsSnapshotRunLogEntry | null;
  recentRuns: AnalyticsSnapshotRunLogEntry[];
  isCurrentlyRunning: boolean;
  perDomain: Record<AnalyticsSnapshotDomain, { lastSuccessfulSnapshotDate: string | null }>;
}

export const analyticsSnapshotService = {
  /** Raw comparison between two explicit dates (YYYY-MM-DD). */
  async compare(domain: AnalyticsSnapshotDomain, currentDate: string, previousDate: string, signal?: AbortSignal): Promise<SnapshotComparisonResult> {
    const r = await apiClient.get<SnapshotComparisonResult>('/api/analytics/snapshot/compare', {
      params: { domain, currentDate, previousDate },
      signal,
    });
    return r.data;
  },

  /** Convenience: compares today's snapshot against `daysAgo` days back (default 7 — a week-over-week trend). */
  async compareToDaysAgo(domain: AnalyticsSnapshotDomain, daysAgo = 7, signal?: AbortSignal): Promise<SnapshotComparisonResult> {
    const now = new Date();
    const previous = new Date(now);
    previous.setUTCDate(previous.getUTCDate() - daysAgo);
    return this.compare(domain, toDateOnly(now), toDateOnly(previous), signal);
  },

  /** Job-health summary (last attempt/success, recent runs, per-domain last-successful-capture date).
   * Requires 'analytics.snapshot.manage' — callers should gate visibility on that permission. */
  async getHealth(signal?: AbortSignal): Promise<SnapshotJobHealth> {
    const r = await apiClient.get<SnapshotJobHealth>('/api/analytics/snapshot/health', { signal });
    return r.data;
  },
};
